<template>
  <div class="opportunity-create">
    <!-- Page-level header: title + subtitle + top-right action pair. -->
    <div class="create-page-header">
      <div>
        <h2 class="create-page-header__title">Add Opportunity</h2>
        <p class="create-page-header__subtitle">
          Create a new opportunity to share with matched alumni.
        </p>
      </div>
      <div class="create-page-header__actions">
        <b-button
          variant="outline-secondary"
          :disabled="submitting"
          @click="cancel"
        >
          Cancel
        </b-button>
        <b-button
          variant="primary"
          class="ml-2"
          :disabled="!isValid || submitting"
          @click="submit"
        >
          <i
            v-if="submitting"
            class="fa fa-spinner fa-spin mr-2"
            aria-hidden="true"
          ></i>
          {{ submitting ? 'Creating…' : 'Create Opportunity' }}
        </b-button>
      </div>
    </div>
    <b-card class="mb-3 section-card">
      <div class="section-title">Job Information</div>
      <div class="row">
        <b-form-group
          label="Job Title *"
          label-for="title"
          class="col-md-6 field-group"
        >
          <b-form-input
            id="title"
            v-model="form.title"
            placeholder="e.g. Software Engineer II"
            :state="touched.title ? !!form.title : null"
            @blur="touched.title = true"
          />
        </b-form-group>

        <b-form-group
          label="Company Name *"
          label-for="company"
          class="col-md-6 field-group"
        >
          <b-form-input
            id="company"
            v-model="form.company"
            placeholder="e.g. Garmin International"
            :state="touched.company ? !!form.company : null"
            @blur="touched.company = true"
          />
        </b-form-group>

        <b-form-group
          label="Location *"
          label-for="location"
          class="col-md-6 field-group"
        >
          <b-form-input
            id="location"
            v-model="form.location"
            placeholder="e.g. Olathe, KS (Hybrid)"
            :state="touched.location ? !!form.location : null"
            @blur="touched.location = true"
          />
        </b-form-group>

        <b-form-group
          label="Job Type *"
          label-for="jobType"
          class="col-md-6 field-group"
        >
          <b-form-select
            id="jobType"
            v-model="form.jobType"
            :options="jobTypeOptions"
            :state="touched.jobType ? !!form.jobType : null"
            @change="touched.jobType = true"
          />
        </b-form-group>

        <b-form-group
          label="Industry"
          label-for="industry"
          class="col-md-6 field-group"
          description="Comma-separate multiple values (e.g. Technology, Healthcare)"
        >
          <b-form-input
            id="industry"
            v-model="industryInput"
            placeholder="e.g. Technology"
          />
        </b-form-group>

        <b-form-group
          label="Application Deadline *"
          label-for="deadline"
          class="col-md-6 field-group"
        >
          <b-form-input
            id="deadline"
            v-model="form.deadline"
            type="date"
            :state="touched.deadline ? !!form.deadline : null"
            @blur="touched.deadline = true"
          />
        </b-form-group>

        <b-form-group
          label="Salary Min"
          label-for="salaryMin"
          class="col-md-6 field-group"
        >
          <b-form-input
            id="salaryMin"
            v-model.number="form.salary.start"
            type="number"
            placeholder="e.g. 75000"
            min="0"
          />
        </b-form-group>

        <b-form-group
          label="Salary Max"
          label-for="salaryMax"
          class="col-md-6 field-group"
        >
          <b-form-input
            id="salaryMax"
            v-model.number="form.salary.end"
            type="number"
            placeholder="e.g. 95000"
            min="0"
          />
        </b-form-group>

        <b-form-group
          label="Required Skills"
          label-for="skills"
          class="col-12 field-group"
          description="Comma-separated (e.g. Python, SQL, Project Management)"
        >
          <b-form-input
            id="skills"
            v-model="skillsInput"
            placeholder="e.g. Python, Data Analysis"
          />
        </b-form-group>

        <b-form-group
          label="Application URL *"
          label-for="applicationUrl"
          class="col-12 field-group"
        >
          <b-form-input
            id="applicationUrl"
            v-model="form.applicationUrl"
            type="url"
            placeholder="https://company.com/careers/apply"
            :state="touched.applicationUrl ? !!form.applicationUrl : null"
            @blur="touched.applicationUrl = true"
          />
        </b-form-group>

        <b-form-group
          label="Required Experience (years)"
          label-for="requiredExperience"
          class="col-md-6 field-group"
        >
          <b-form-input
            id="requiredExperience"
            v-model.number="form.requiredExperienceYears"
            type="number"
            placeholder="e.g. 3"
            min="0"
          />
        </b-form-group>

        <b-form-group
          label="Required Education"
          label-for="requiredEducation"
          class="col-md-6 field-group"
        >
          <b-form-select
            id="requiredEducation"
            v-model="form.requiredEducation"
            :options="educationOptions"
          />
        </b-form-group>
      </div>
    </b-card>
    <b-card class="mb-3 section-card">
      <div class="section-title">Description &amp; Notes</div>
      <b-form-group
        label="Job Description *"
        label-for="description"
        description="Include key responsibilities, requirements, and any benefits."
      >
        <b-form-textarea
          id="description"
          v-model="form.description"
          rows="6"
          max-rows="14"
          placeholder="Paste or write the full job description here…"
          :state="touched.description ? !!form.description : null"
          @blur="touched.description = true"
        />
      </b-form-group>
    </b-card>
    <div class="create-page-footer">
      <b-button
        variant="outline-secondary"
        :disabled="submitting"
        @click="cancel"
      >
        Cancel
      </b-button>
      <b-button
        variant="primary"
        class="ml-2"
        :disabled="!isValid || submitting"
        @click="submit"
      >
        <i
          v-if="submitting"
          class="fa fa-spinner fa-spin mr-2"
          aria-hidden="true"
        ></i>
        {{ submitting ? 'Creating…' : 'Create Opportunity' }}
      </b-button>
    </div>
  </div>
</template>

<script>
import axios from 'axios'
import { createMockOpportunity } from './mocks/opportunities'
import { CORE_API_URL, dashboardDraftToCoreApi } from './opportunityAdapter'

// VUE_APP_USE_OPPORTUNITIES_MOCKS=true falls back to the in-memory mocks.
// Default posts to core-api at VUE_APP_CORE_API_URL.
const USE_MOCKS = process.env.VUE_APP_USE_OPPORTUNITIES_MOCKS === 'true'

// Fallback community when no selector value is available. TODO: replace
// once Hermes config drives the active community.
const DEFAULT_ADMIN_COMMUNITY_ID = 'community-alpha'

// Dashboard form uses Title-Case ('Full-Time'); core-api uses lowercase.
const JOB_TYPE_TO_CORE_API = {
  'Full-Time': 'full-time',
  'Part-Time': 'part-time',
  Hourly: 'hourly',
  Contract: 'contract',
  Internship: 'internship',
}

const JOB_TYPE_OPTIONS = [
  { value: null, text: 'Select type', disabled: true },
  { value: 'Full-Time', text: 'Full-Time' },
  { value: 'Part-Time', text: 'Part-Time' },
  { value: 'Hourly', text: 'Hourly' },
  { value: 'Contract', text: 'Contract' },
  { value: 'Internship', text: 'Internship' },
]

const EDUCATION_OPTIONS = [
  { value: null, text: 'No requirement' },
  { value: 'high-school', text: 'High School' },
  { value: 'bachelors', text: "Bachelor's" },
  { value: 'masters', text: "Master's" },
]

export default {
  name: 'OpportunityCreate',
  data() {
    return {
      submitting: false,
      jobTypeOptions: JOB_TYPE_OPTIONS,
      educationOptions: EDUCATION_OPTIONS,
      industryInput: '',
      skillsInput: '',
      form: {
        title: '',
        company: '',
        location: '',
        jobType: null,
        deadline: '',
        applicationUrl: '',
        description: '',
        requiredExperienceYears: null,
        requiredEducation: null,
        salary: {
          start: null,
          end: null,
        },
      },
      touched: {
        title: false,
        company: false,
        location: false,
        jobType: false,
        deadline: false,
        applicationUrl: false,
        description: false,
      },
    }
  },
  computed: {
    isValid() {
      const f = this.form
      return !!(
        f.title &&
        f.company &&
        f.location &&
        f.jobType &&
        f.deadline &&
        f.applicationUrl &&
        f.description
      )
    },
  },
  methods: {
    parseList(s) {
      if (!s) return []
      return s
        .split(',')
        .map((x) => x.trim())
        .filter(Boolean)
    },
    cancel() {
      this.$router.push('/engagement/opportunities')
    },
    async submit() {
      if (!this.isValid || this.submitting) return
      this.submitting = true

      const adminUser = (() => {
        try {
          return JSON.parse(localStorage.getItem('authUser')) || {}
        } catch (_) {
          return {}
        }
      })()

      const payload = {
        ...this.form,
        industry: this.parseList(this.industryInput),
        skills: this.parseList(this.skillsInput),
        submitter: {
          firstName: adminUser.firstName || 'Admin',
          lastName: adminUser.lastName || 'User',
          email: adminUser.email || '',
        },
      }

      if (USE_MOCKS) {
        const created = createMockOpportunity(payload)
        this.submitting = false
        this.$router.push(
          `/engagement/opportunities/${created._id}`,
        )
        return
      }

      try {
        // Adapter drops unsupported fields and builds the CreateOpportunityDto payload.
        const communityId =
          this.$route.params.community_id ||
          localStorage.getItem('user_modal_option') ||
          DEFAULT_ADMIN_COMMUNITY_ID
        // Split location "City, ST" into a structured object.
        const locationParts = (this.form.location || '').split(',').map((p) => p.trim())
        const draft = {
          title: payload.title,
          company: payload.company,
          description: payload.description,
          applicationUrl: payload.applicationUrl,
          type: JOB_TYPE_TO_CORE_API[payload.jobType] || 'full-time',
          deadline: payload.deadline,
          location:
            locationParts[0] || locationParts[1]
              ? {
                  city: locationParts[0] || undefined,
                  state: locationParts[1] || undefined,
                  country: 'USA',
                  remote: false,
                }
              : undefined,
          salary: payload.salary,
          industry: payload.industry,
          skills: payload.skills,
          requiredExperienceYears: payload.requiredExperienceYears,
          requiredEducation: payload.requiredEducation,
          source: {
            channel: 'dashboard',
            firstName: payload.submitter.firstName,
            lastName: payload.submitter.lastName,
            email: payload.submitter.email,
          },
        }
        const body = dashboardDraftToCoreApi(draft, communityId)
        const authToken = localStorage.getItem('id_token')
        const { data } = await axios.post(
          `${CORE_API_URL}/api/opportunities`,
          body,
          { headers: authToken ? { Authorization: `Bearer ${authToken}` } : {} },
        )
        this.$router.push(`/engagement/opportunities/${data._id || data.id}`)
      } catch (err) {
        if (err.response && err.response.status === 401) {
          localStorage.removeItem('authUser')
          this.$router.push('/login')
          return
        }
        alert(
          'We couldn\'t create the opportunity. Please try again in a moment.',
        )
      } finally {
        this.submitting = false
      }
    },
  },
}
</script>

<style scoped>
.opportunity-create {
  padding-top: 0.5rem;
}
.create-page-header {
  display: flex;
  justify-content: space-between;
  align-items: flex-start;
  margin-bottom: 1.25rem;
  gap: 1.5rem;
  flex-wrap: wrap;
}
.create-page-header__title {
  font-size: 1.5rem;
  font-weight: 600;
  margin: 0 0 0.25rem 0;
  color: #fff;
}
.create-page-header__subtitle {
  margin: 0;
  color: var(--text-muted, #9aa0a6);
  font-size: 0.95rem;
}
.create-page-header__actions {
  flex-shrink: 0;
  display: flex;
  align-items: center;
}
.section-card {
  border: none !important;
}
.section-title {
  font-size: 0.75rem;
  font-weight: 600;
  text-transform: uppercase;
  letter-spacing: 0.08em;
  color: var(--text-muted, #9aa0a6);
  margin-bottom: 1rem;
}
.field-group {
  margin-bottom: 1rem;
}
.create-page-footer {
  display: flex;
  justify-content: flex-end;
  align-items: center;
  margin-top: 1.5rem;
  padding-top: 1rem;
  border-top: 1px solid rgba(255, 255, 255, 0.06);
}
::v-deep .field-group label {
  font-size: 0.75rem;
  font-weight: 600;
  text-transform: uppercase;
  letter-spacing: 0.04em;
  color: rgba(255, 255, 255, 0.7);
}
::v-deep .field-group .form-text {
  font-size: 0.75rem;
  color: rgba(255, 255, 255, 0.45);
  margin-top: 0.25rem;
}
</style>
